import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

export function McpsTable({
  mcps,
  onToggleStatus,
  onEditAccess,
  onEdit,
  onDelete,
  isUpdating,
  isDeleting,
  disableAll,
}: {
  mcps: Array<{
    label: string
    url: string
    allowed_tools: string[] | null
    require_approval: string
    enabled: boolean
    source: "config" | "override" | "dynamic"
    access_configured?: boolean
  }>
  onToggleStatus: (label: string, enabled: boolean) => void
  onEditAccess?: (label: string) => void
  /** Open the Form+Raw edit dialog for this MCP server. */
  onEdit?: (label: string) => void
  onDelete: (label: string) => void
  isUpdating?: string
  isDeleting?: string
  disableAll?: boolean
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const controls = useAdminTableControls(mcps, {
    getName: (row) => row.label,
    getSearchText: (row) => [row.label, row.url, ...(row.allowed_tools ?? [])].join(" "),
    isEnabled: (row) => row.enabled,
  })
  const displayMcps = isExpanded ? controls.rows : controls.rows.slice(0, 5)
  const hasMoreMcps = controls.count > 5

  if (mcps.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.mcps.emptyDescription", "No MCP servers configured")}
        icon={Server}
        title={t("adminDashboard.mcps.emptyTitle", "No MCP Servers")}
      />
    )
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar bulkActions controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.mcps.label", "Label")}</div>
        <div className="col-span-3">{t("adminDashboard.mcps.url", "URL")}</div>
        <div className="col-span-2">{t("adminDashboard.mcps.allowedTools", "Tools")}</div>
        <div className="col-span-2">{t("adminDashboard.mcps.approval", "Requires Approval")}</div>
        <div className="col-span-1 text-right">
          {t("adminDashboard.access.access", { defaultValue: "Access" })}
        </div>
        <div className="col-span-1 text-right">{t("adminDashboard.mcps.status", "Status")}</div>
      </div>

      {displayMcps.map((mcp) => (
        <div
          key={mcp.label}
          className="admin-responsive-grid-row group bg-card hover:bg-accent/50 relative grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
        >
          <div className="col-span-3 flex flex-col">
            <span className="font-medium">{mcp.label}</span>
            <span className="text-muted-foreground text-xs">{mcp.source}</span>
          </div>
          <div className="text-muted-foreground col-span-3 flex items-center truncate font-mono text-xs">
            <span className="text-foreground mr-3 w-24 shrink-0 font-sans font-medium lg:hidden">
              URL
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help truncate">{mcp.url}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs break-all">{mcp.url}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="col-span-2 flex items-center">
            <span className="text-muted-foreground mr-3 w-24 shrink-0 text-xs font-medium lg:hidden">
              Tools
            </span>
            {mcp.allowed_tools && mcp.allowed_tools.length > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground cursor-help">
                      {mcp.allowed_tools.length === 1
                        ? mcp.allowed_tools[0]
                        : `${mcp.allowed_tools.length} tools`}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{mcp.allowed_tools.join(", ")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-muted-foreground">
                {t("adminDashboard.mcps.allTools", "All tools")}
              </span>
            )}
          </div>
          <div className="col-span-2 flex items-center gap-1.5">
            <span className="text-muted-foreground mr-3 w-24 shrink-0 text-xs font-medium lg:hidden">
              Approval
            </span>
            {mcp.require_approval === "never" ? (
              <span className="text-muted-foreground">
                {t("adminDashboard.mcps.approvalNo", "No")}
              </span>
            ) : (
              <>
                <ShieldAlert className="text-warning-ink size-3.5" />
                <span className="text-muted-foreground">
                  {t("adminDashboard.mcps.approvalYes", "Yes")}
                </span>
              </>
            )}
          </div>
          <div className="col-span-1 flex items-center justify-end gap-1">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Actions
            </span>
            {onEdit ? (
              <Button
                disabled={disableAll}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.mcps.editAriaLabel", {
                  defaultValue: "Edit MCP server",
                })}
                onClick={() => onEdit(mcp.label)}
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
            <Button
              aria-label={t("adminDashboard.access.access", { defaultValue: "Access" })}
              disabled={disableAll || !onEditAccess}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => onEditAccess?.(mcp.label)}
            >
              {mcp.access_configured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
            </Button>
          </div>
          <div className="col-span-1 flex items-center justify-end">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Status
            </span>
            <Switch
              aria-label={`${mcp.label} status`}
              checked={mcp.enabled}
              disabled={disableAll || isUpdating === mcp.label}
              onCheckedChange={(checked) => onToggleStatus(mcp.label, checked)}
            />
          </div>
          {mcp.source === "dynamic" && (
            <Button
              className="absolute top-2 right-2 opacity-100 transition-opacity lg:top-1/2 lg:-translate-y-1/2 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100"
              disabled={disableAll || isDeleting === mcp.label}
              size="icon"
              variant="ghost"
              aria-label={t("adminDashboard.mcps.deleteAriaLabel", {
                defaultValue: "Delete MCP server",
              })}
              onClick={() => onDelete(mcp.label)}
            >
              <Trash2 className="text-destructive size-4" />
            </Button>
          )}
        </div>
      ))}

      {hasMoreMcps && (
        <div className="flex justify-center pt-2">
          <Button className="w-full" variant="outline" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.mcps.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.mcps.showMore", "Show More", {
                  count: controls.count - 5,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
